import React from 'react';
import { motion } from 'framer-motion';
import FuturisticCard from '../../../components/ui/FuturisticCard';
import HolographicText from '../../../components/ui/HolographicText';
import ParticleSystem from '../../../components/ui/ParticleSystem';
import FloatingElements from '../../../components/ui/FloatingElements';

const LeadershipContact = () => {
  const leaders = [
    {
      name: 'Saurabh Joshi',
      email: 'saurabh@onsmettle.com',
      phone: '+91 78973 77639',
      role: 'Business & Operations Head',
      description: 'Strategic leadership and business operations oversight',
      icon: '👨‍💼',
      color: 'text-brand-cyan'
    },
    {
      name: 'Rahul Kumar',
      email: 'rahul@onsmettle.com',
      phone: '+91 96319 49041',
      role: 'Technical Director',
      description: 'Technical excellence and project delivery leadership',
      icon: '⚙️',
      color: 'text-brand-green'
    }
  ];

  return (
    <section className="py-16 relative overflow-hidden bg-gradient-to-b from-primary via-secondary to-brand-navy">
      {/* Background Effects */}
      <ParticleSystem
        particleCount={40}
        color="#00D4FF"
        size={2}
        speed={0.2}
        className="opacity-30"
      />
      <FloatingElements count={6} className="opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <HolographicText
            size="text-2xl sm:text-3xl lg:text-4xl"
            className="mb-4"
            animate={true}
          >
            Leadership Contact
          </HolographicText>
          <p className="text-white/80 font-body max-w-2xl mx-auto text-lg">
            Connect directly with our leadership team for strategic discussions and partnership opportunities
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {leaders.map((leader, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <FuturisticCard
                variant="primary"
                glow={true}
                className="p-8 h-full hover:scale-105 transition-transform duration-300"
              >
                <div className="text-center mb-6">
                  <div className={`text-5xl mb-4 ${leader.color}`}>
                    {leader.icon}
                  </div>
                  <h3 className="text-2xl font-bold font-heading mb-2 text-black">
                    {leader.name}
                  </h3>
                  <p className={`text-lg font-semibold ${leader.color} mb-2`}>
                    {leader.role}
                  </p>
                  <p className="text-black font-body text-sm">
                    {leader.description}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-3 p-3 glass-morph rounded-lg">
                    <span className="text-brand-orange text-xl">📧</span>
                    <a 
                      href={`mailto:${leader.email}`}
                      className=" hover:text-brand-cyan transition-colors duration-300 font-body"
                    >
                      {leader.email}
                    </a>
                  </div>

                  <div className="flex items-center justify-center space-x-3 p-3 glass-morph rounded-lg">
                    <span className="text-brand-green text-xl">📱</span>
                    <a 
                      href={`tel:${leader.phone}`}
                      className="text-primary hover:text-brand-green transition-colors duration-300 font-body"
                    >
                      {leader.phone}
                    </a>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <motion.button
                    className="px-6 py-3 glass-morph bg-brand-cyan/20 hover:bg-brand-cyan/30 text-primary rounded-xl font-semibold border border-brand-cyan/50 neon-glow transition-all duration-300 backdrop-blur-md"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = `mailto:${leader.email}`}
                  >
                    Contact {leader.name.split(' ')[0]}
                  </motion.button>
                </div>
              </FuturisticCard>
            </motion.div>
          ))}
        </div>

        {/* Additional Contact Information */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: true }}
        >
         <FuturisticCard variant="secondary" className="p-8 max-w-4xl mx-auto">
            <h4 className="text-gray-900 text-xl font-bold font-heading mb-4 ">
              Direct Leadership Access
            </h4>
            <p className="text-gray-700 font-body leading-relaxed">
              Our leadership team is committed to{' '}
              <span className="text-brand-cyan font-semibold">direct client engagement</span>{' '}
              and strategic partnership development. Whether you're looking to discuss{' '}
              <span className="text-brand-green font-semibold">large-scale government projects</span>,{' '}
              explore{' '}
              <span className="text-brand-orange font-semibold">innovative telecommunications solutions</span>,{' '}
              or establish{' '}
              <span className="text-brand-purple font-semibold">long-term partnerships</span>,{' '}
              our leaders are available for direct consultation.
            </p>
          </FuturisticCard>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadershipContact;
